/**
 * Shared file type configuration
 *
 * This is the single source of truth for supported file types across the entire application.
 * Both the server (multer config) and client (media library) use this configuration.
 *
 * Located in @citychallenge/core to ensure compatibility with both Node.js and React environments.
 */

export interface FileTypeConfig {
  extensions: readonly string[];
  mimeTypes: readonly string[];
  maxSizeMB: number;
  maxWidth?: number;
  label: string;
  icon?: string;
}

// Image files (10 MB limit)
export const IMAGE_CONFIG: FileTypeConfig = {
  extensions: [".png", ".jpg", ".jpeg", ".gif", ".webp"],
  mimeTypes: ["image/png", "image/jpeg", "image/gif", "image/webp"],
  maxSizeMB: 10,
  label: "Images",
  icon: "far fa-image",
} as const;

// Video files (100 MB limit, max 5120px wide)
// The 5120px width cap protects the transform/imgproxy preview-thumbnail
// pipeline (transform.intractive.app), which crashes on videos wider than
// ~6000px (notably 6K/8K equirectangular sources used in VR Branch hotspots).
export const VIDEO_CONFIG: FileTypeConfig = {
  extensions: [".mp4", ".mov", ".webm"],
  mimeTypes: ["video/mp4", "video/quicktime", "video/webm"],
  maxSizeMB: 100,
  maxWidth: 5120,
  label: "Videos",
  icon: "far fa-video",
} as const;

// SVG files (1 MB limit)
export const SVG_CONFIG: FileTypeConfig = {
  extensions: [".svg"],
  mimeTypes: ["image/svg+xml"],
  maxSizeMB: 1,
  label: "SVG Images",
  icon: "far fa-image",
} as const;

// Font files (1 MB limit)
export const FONT_CONFIG: FileTypeConfig = {
  extensions: [".ttf", ".otf", ".woff", ".woff2"],
  mimeTypes: [
    "application/octet-stream",
    "application/x-font-ttf",
    "font/ttf",
    "font/otf",
    "application/vnd.ms-opentype",
    "font/woff",
    "font/woff2",
  ],
  maxSizeMB: 1,
  label: "Fonts",
  icon: "far fa-font",
} as const;

// PDF files (10 MB limit)
export const PDF_CONFIG: FileTypeConfig = {
  extensions: [".pdf"],
  mimeTypes: ["application/pdf", "application/x-pdf"],
  maxSizeMB: 10,
  label: "PDFs",
  icon: "far fa-file-pdf",
} as const;

// Word Document files (10 MB limit)
export const DOC_CONFIG: FileTypeConfig = {
  extensions: [".doc", ".docx"],
  mimeTypes: [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  maxSizeMB: 10,
  label: "Word Documents",
  icon: "far fa-file-word",
} as const;

// Audio files (10 MB limit)
export const AUDIO_CONFIG: FileTypeConfig = {
  extensions: [".mp3"],
  mimeTypes: ["audio/mp3", "audio/mpeg"],
  maxSizeMB: 10,
  label: "Audio",
  icon: "far fa-music",
} as const;

// Subtitle files (1 MB limit)
export const SUBTITLE_CONFIG: FileTypeConfig = {
  extensions: [".srt", ".vtt"],
  mimeTypes: ["text/vtt", "text/srt", "text/plain", "application/octet-stream"],
  maxSizeMB: 1,
  label: "Subtitles",
  icon: "far fa-closed-captioning",
} as const;

// 3D Object files (20 MB limit)
export const OBJECT_CONFIG: FileTypeConfig = {
  extensions: [".gltf", ".glb"],
  mimeTypes: [],
  maxSizeMB: 20,
  label: "3D Objects",
  icon: "far fa-cube",
} as const;

// All file type configurations
export const FILE_TYPE_CONFIGS = {
  image: IMAGE_CONFIG,
  video: VIDEO_CONFIG,
  svg: SVG_CONFIG,
  font: FONT_CONFIG,
  pdf: PDF_CONFIG,
  doc: DOC_CONFIG,
  audio: AUDIO_CONFIG,
  subtitle: SUBTITLE_CONFIG,
  object: OBJECT_CONFIG,
} as const;

// Derived arrays for convenience
export const ALL_SUPPORTED_EXTENSIONS = Object.values(
  FILE_TYPE_CONFIGS
).flatMap((config) => config.extensions);

export const MEDIA_EXTENSIONS = [
  ...IMAGE_CONFIG.extensions,
  ...VIDEO_CONFIG.extensions,
  ...SVG_CONFIG.extensions,
];

// Helper functions
export function isImageExtension(ext: string): boolean {
  return IMAGE_CONFIG.extensions.includes(
    ext as (typeof IMAGE_CONFIG.extensions)[number]
  );
}

export function isVideoExtension(ext: string): boolean {
  return VIDEO_CONFIG.extensions.includes(
    ext as (typeof VIDEO_CONFIG.extensions)[number]
  );
}

export function isSVGExtension(ext: string): boolean {
  return SVG_CONFIG.extensions.includes(
    ext as (typeof SVG_CONFIG.extensions)[number]
  );
}

export function isMediaExtension(ext: string): boolean {
  return MEDIA_EXTENSIONS.includes(ext as (typeof MEDIA_EXTENSIONS)[number]);
}

export function getFileTypeConfig(extension: string): FileTypeConfig | null {
  for (const config of Object.values(FILE_TYPE_CONFIGS)) {
    if (config.extensions.includes(extension as any)) {
      return config;
    }
  }
  return null;
}

// Type exports
export type FileTypeKey = keyof typeof FILE_TYPE_CONFIGS;
export type SupportedExtension = (typeof ALL_SUPPORTED_EXTENSIONS)[number];
export type MediaExtension = (typeof MEDIA_EXTENSIONS)[number];
